import { EntityDto } from "@sharedcommon/rest/identity.restservice";

export class OperatingModeUtil {
  public static SAMPLE_MODE = "SAMPLE";
  public static FEED_MODE = "FEED";
  private static ALLOWED_OPERATING_MODE = [ this.SAMPLE_MODE, this.FEED_MODE ]

  private static MAP_FOR_TRANSLATION_KEY: Map<string, string> = new Map<string, string>([
    [ this.SAMPLE_MODE, "label.common.operating-mode.sample" ],
    [ this.FEED_MODE, "label.common.operating-mode.feed" ]
  ]);

  /**
   * Return the key to translate according the operating mode
   * @param operatingMode the operating mode to translate
   * @returns the key to translate
   */
  static returnTranslationKey(operatingMode: string): string {
    return this.MAP_FOR_TRANSLATION_KEY.get(operatingMode);
  }

  /**
   * Return the translation key for sample
   * @returns the key to translate for sample
   */
  static returnSampleTranslationKey(): string {
    return this.returnTranslationKey(this.SAMPLE_MODE);
  }

  /**
   * Return the translation key for feed
   * @returns the key to translate for feed
   */
  static returnFeedTranslationKey(): string {
    return this.returnTranslationKey(this.FEED_MODE);
  }

  /**
   * Return the list of allowed operating mode from entity
   * @param entity the entity from what we want the operating mode
   * @returns the list of allowed operating mode
   */
  static returnAllowedOperatingModesOfEntity(entity: EntityDto): string[] {
    if (!entity?.operatingModes) {
      return [];
    }
    return this.returnAllOperatingModeOfEntity(entity).filter((opMode) => this.isAllowedOperatingMode(opMode));
  }

  /**
   * Check if the entity has a specific operating mode
   * @param entity the entity from what we want the operating mode
   * @param opModeToCheck the operating mode to check
   * @returns true if the entity has this operating mode
   */
  static isEntityHasOperatingMode(entity: EntityDto, opModeToCheck: string): boolean {
    return this.isThereSpecificOperatingMode(this.returnAllOperatingModeOfEntity(entity), opModeToCheck);
  }

  /**
   * Return the list all operating mode (allowed or not) from entity
   * @param entity the entity from what we want the operating mode
   * @returns the list of all operating mode (allowed one or not)
   */
  private static returnAllOperatingModeOfEntity(entity: EntityDto): string[] {
    if (!entity?.operatingModes) {
      return [];
    }
    return entity.operatingModes.split('|');
  }

  /**
   * Set the attribute for operating mode of the entity
   * @param entity the entity to update
   * @param isSample at true if the operating mode must include sample mode
   * @param isFeed at true if the operating mode must include the feed mode
   */
  static setOperatingModesForEntity(entity: EntityDto, isSample: boolean, isFeed: boolean): void {
    const allOpModes: string[] = isSample ? [ this.SAMPLE_MODE ] : [];
    if (isFeed) {
      allOpModes.push(this.FEED_MODE);
    }
    entity.operatingModes = allOpModes.join('|');
  }

  /**
   * Check is the operating mode is for a SAMPLE mode
   * @param operatingMode the operating mode to check
   * @returns true if the operating mode is a SAMPLE one
   */
  static isSampleOperatingMode(operatingMode: string): boolean {
    return operatingMode === this.SAMPLE_MODE;
  }

  /**
   * Check is the operating mode is for a FEED mode
   * @param operatingMode the operating mode to check
   * @returns true if the operating mode is a FEED one
   */
  static isFeedOperatingMode(operatingMode: string): boolean {
    return operatingMode === this.FEED_MODE;
  }

  /**
   * Check if in a list of operating mode there is a FEED one
   * @param allOpMode the list of operating mode to check
   * @returns true if in the list there is the FEED operating mode
   */
  static isThereFeedOperatingMode(allOpMode: string[]): boolean {
    return this.isThereSpecificOperatingMode(allOpMode, this.FEED_MODE);
  }

  /**
   * Check if in a list of operating mode there is a SAMPLE one
   * @param allOpMode the list of operating mode to check
   * @returns true if in the list there is the SAMPLE operating mode
   */
  static isThereSampleOperatingMode(allOpMode: string[]): boolean {
    return this.isThereSpecificOperatingMode(allOpMode, this.SAMPLE_MODE);
  }

  /**
   * Check if in a list of operating mode there is the one to look for
   * @param allOpMode the list of operating mode to check
   * @param opModeToLookFor the operating mode to look for
   * @returns true if in the list there is the one to look for
   */
  private static isThereSpecificOperatingMode(allOpMode: string[], opModeToLookFor: string): boolean {
    return allOpMode.some(opMode => opMode === opModeToLookFor);
  }

  /**
   * Check if the operating mode is an allowed one
   * @param operatingMode the operating mode to check
   * @returns true if the operating mode is an allowed one
   */
  static isAllowedOperatingMode(operatingMode: string): boolean {
    return !!operatingMode && this.ALLOWED_OPERATING_MODE.includes(operatingMode);
  }
}